import { useState, useCallback } from 'react';

export interface AlertOptions {
  title?: string;
  type?: 'success' | 'error' | 'warning' | 'info';
  confirmText?: string;
  showCancel?: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
}

export interface AlertState extends AlertOptions {
  open: boolean;
  message: string;
}

export const useAlert = () => {
  const [alertState, setAlertState] = useState<AlertState>({
    open: false,
    message: '',
    type: 'info',
  });

  const showAlert = useCallback((message: string, options: AlertOptions = {}) => {
    setAlertState({
      open: true,
      message,
      ...options,
    });
  }, []);

  const showSuccess = useCallback((message: string, options: Omit<AlertOptions, 'type'> = {}) => {
    showAlert(message, { ...options, type: 'success' });
  }, [showAlert]);

  const showError = useCallback((message: string, options: Omit<AlertOptions, 'type'> = {}) => {
    showAlert(message, { ...options, type: 'error' });
  }, [showAlert]);

  const showWarning = useCallback((message: string, options: Omit<AlertOptions, 'type'> = {}) => {
    showAlert(message, { ...options, type: 'warning' });
  }, [showAlert]);

  const showInfo = useCallback((message: string, options: Omit<AlertOptions, 'type'> = {}) => {
    showAlert(message, { ...options, type: 'info' });
  }, [showAlert]);

  const showConfirm = useCallback((message: string, onConfirm: () => void, options: Omit<AlertOptions, 'onConfirm' | 'showCancel'> = {}) => {
    showAlert(message, {
      ...options,
      showCancel: true,
      onConfirm,
      confirmText: options.confirmText || 'Confirm',
    });
  }, [showAlert]);

  const hideAlert = useCallback(() => {
    setAlertState(prev => ({ ...prev, open: false }));
  }, []);

  return {
    alertState,
    showAlert,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showConfirm,
    hideAlert,
  };
};

export default useAlert;
